import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Checkbox, Form, Grid } from 'semantic-ui-react'
import CO2Service from '~/api/services/co2'
import { FormFields } from '~/components/FormFields'
import { formatNumber, handleErrors } from '~/utils/helpers'
import { MilkProductionField, StyledButton, StyledField } from '../style'
import RaceSlider from './RaceSlider'
import BlackCow from '@assets/images/black-cow.jpg'
import BrowCow from '@assets/images/brown-cow.jpg'
import GrayCow from '@assets/images/gray-cow.jpg'
import BrowWhiteCow from '@assets/images/brown-white-cow.jpg'
import InfoPopup from '~/components/InfoPopup'
import { KeyLabelType } from '~/types'

interface Props {
  setIsLoadingResults: (value: boolean) => void
  breeds: { id: number, label: string }[]
  housingTypes: KeyLabelType[]
}

const MilkQuantity: React.FC<Props> = (props) => {
  const { setIsLoadingResults, breeds, housingTypes } = props
  const { t } = useTranslation()
  const { control, reset, getValues, setValue } = useFormContext()
  const { id, farmerId } = useParams()
  const [isLoadingPredefinedData, setIsLoadingPredefinedData] = useState(false)
  const watchBreedField = useWatch({ control, name: 'breeds' }) || []
  const watchTaxField = useWatch({ control, name: 'tax' })
  const watchCowsNumber = useWatch({ control, name: 'cowsNumber' })
  const watchTotalMilkAmount = useWatch({ control, name: 'totalMilkAmount' })
  const watchDryPeriod = useWatch({ control, name: 'feedGroups[0].days' })
  const watchCalvingPeriod = useWatch({ control, name: 'calvingPeriod' })
  const watchOtherMilk = useWatch({ control, name: 'otherMilkAmount' })
  const values = getValues()
  const {
    milkPrice,
    fat,
    protein,
    totalMilkAmount
  } = values

  useEffect(() => {
    const feedGroups = getValues('feedGroups')
    if (feedGroups) {
      const value = feedGroups?.reduce((previousValue, currentValue, index) => {
        if (index !== 1) {
          return previousValue - currentValue['days']
        }
        return previousValue
      }, watchCalvingPeriod)
      setValue('feedGroups[1].days', value)
    }
  }, [watchCalvingPeriod, watchDryPeriod])

  async function fetchPredefinedData(value) {
    setIsLoadingPredefinedData(true)
    setIsLoadingResults(true)
    try {
      const formValues = getValues()
      const response = await CO2Service.getPredefinedData(id, { breeds: value, tax: watchTaxField, farmerId })
      reset({
        ...formValues,
        ...response.data,
        calfRearing: '',
        cowsNumber: '',
        breeds: value,
        departureRate: '',
        calvingPeriod: '',
        manureType: '',
        energyConsumptions: [],
        manureStorageTypes: [],
        phaseFeeding: 0,
        feedingPhases: []
      })
      setValue('breeds', value)
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingPredefinedData(false)
    setIsLoadingResults(false)
  }

  function getMilkYealdValue() {
    const otherMilk = !watchOtherMilk || isNaN(watchOtherMilk) ? 0 : parseInt(watchOtherMilk)
    const transformedCowsNumber = watchCowsNumber.toString().replace(',', '.')
    const value = transformedCowsNumber > 0 && !isNaN(transformedCowsNumber) ? formatNumber(Math.floor((parseInt(watchTotalMilkAmount) + otherMilk) / transformedCowsNumber), 0) : 0
    return t('{{value}} kg pro Kuh u. Jahr', { value })
  }

  function renderFields() {
    return <div style={{ marginTop: '2em' }}>
      <Grid columns={4}>
        <Grid.Row>
          <Grid.Column>
            <FormFields.Input
              control={control}
              name={'cowsNumber'}
              suffix={' Milchkühe'}
              withoutAsterisk
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <label>{t('⌀ inventory')}</label>
                <InfoPopup
                  content={t('This information can be found in the LKV annual report, Qcontrol.')}
                  styleIcon={{ fontSize: '1.2em' }}
                  position={'top center'}
                />
              </div>}
              typeInput='controled'
              defaultValue={0}
              allowDecimals={true}
              noErrorMessage
              decimalScale={1}
              rules={{
                validate: (value) => {
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 3 || transformValue > 6000) {
                    return false
                  }
                  return true
                },
                required: true
              }}
              placeholder={t('Number of dairy cows')}
            />
          </Grid.Column>
          <Grid.Column>
            <StyledField>
              <label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div>{t('Calving interval')}</div>
                  <InfoPopup
                    content={t('Period between two calvings / standard value, depending on the breed, between 390 and 420 days')}
                    styleIcon={{ fontSize: '1.2em' }}
                    position={'top center'}
                  />
                </div>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
                <StyledButton
                  type='button'
                  icon='minus'
                  disabled={watchCalvingPeriod === 1 || !watchCalvingPeriod}
                  onClick={() => setValue('calvingPeriod', parseInt(watchCalvingPeriod) - 1)}
                />
                <FormFields.Input
                  control={control}
                  name={'calvingPeriod'}
                  style={{ textAlign: 'center' }}
                  typeInput='controled'
                  allowDecimals={false}
                  withoutAsterisk
                  noErrorMessage
                  suffix=' Tage'
                  rules={{
                    validate: (value) => {
                      const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                      if (transformValue < 300 || transformValue > 551) {
                        return false
                      }
                      return true
                    },
                    required: true
                  }}
                  placeholder={t('in days')}
                />
                <StyledButton
                  type='button'
                  onClick={() => {
                    const number = !watchCalvingPeriod || isNaN(watchCalvingPeriod) ? 0 : parseInt(watchCalvingPeriod)
                    setValue('calvingPeriod', number + 1)
                  }}
                  icon='add'
                />
              </div>
            </StyledField>
          </Grid.Column>
          <Grid.Column>
            <FormFields.Input
              control={control}
              name={'averageLiveWeight'}
              typeInput='controled'
              allowDecimals={true}
              withoutAsterisk
              rules={{
                validate: (value) => {
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 400 || transformValue > 1500) {
                    return false
                  }
                  return true
                },
                required: true
              }}
              noErrorMessage
              decimalScale={1}
              suffix=' kg pro Kuh'
              label={t('Ø live weight')}
              placeholder={t('in kg pro Kuh')}
            />
          </Grid.Column>
          <Grid.Column>
            <FormFields.Input
              control={control}
              name={'departureRate'}
              withoutAsterisk
              suffix=' %'
              allowDecimals
              typeInput={'controled'}
              rules={{
                validate: (value) => {
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 10 || transformValue > 60) {
                    return false
                  }
                  return true
                },
                required: true
              }}
              noErrorMessage
              decimalScale={2}
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <label>{t('Attrition rate')}</label>
                <InfoPopup
                  content={t('This field represents both the exit rate (e.g. slaughter cow sales) and the herd replenishment rate. Based on this number,  \n\
                  emissions for the slaughter cows and the herd replenishment are calculated. \n\
                  If there are differences between the departure rate and the replacement rate in your company, please enter the average.')}
                  styleIcon={{ fontSize: '1.2em' }}
                  position={'top center'}
                />
              </div>}
              placeholder={t('in %')}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column>
            <MilkProductionField>
              <label>{t('Milk yield')}</label>
              <div>{getMilkYealdValue()}</div>
            </MilkProductionField>
          </Grid.Column>
          <Grid.Column>
            <StyledField>
              <label>{t('Dry period')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
                <StyledButton
                  type='button'
                  icon='minus'
                  disabled={watchDryPeriod === 30 || !watchDryPeriod}
                  onClick={() => setValue('feedGroups[0].days', parseInt(watchDryPeriod) - 1)}
                />
                <FormFields.Input
                  control={control}
                  suffix=' Tage'
                  name={'feedGroups[0].days'}
                  withoutAsterisk
                  noErrorMessage
                  rules={{
                    validate: (value) => {
                      const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                      if (transformValue < 30 || transformValue > 81) {
                        return false
                      }
                      return true
                    },
                    required: true
                  }}
                  typeInput={'controled'}
                  placeholder={t('in days')}
                />
                <StyledButton
                  type='button'
                  disabled={watchDryPeriod === 70}
                  onClick={() => {
                    const number = isNaN(watchDryPeriod) ? 0 : parseInt(watchDryPeriod)
                    setValue('feedGroups[0].days', number + 1)
                  }}
                  icon='add'
                />
              </div>
            </StyledField>
          </Grid.Column>
          <Grid.Column>
            <FormFields.Input
              control={control}
              typeInput='controled'
              allowDecimals={true}
              withoutAsterisk
              decimalScale={1}
              rules={{
                validate: (value) => {
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 200 || transformValue > 800) {
                    return false
                  }
                  return true
                },
                required: true
              }}
              noErrorMessage
              suffix=' kg pro Altkuh'
              name={'slaughterCowWeight'}
              label={t('Old cow slaughter weight')}
              placeholder={t('in kg pro Kuh')}
            />
          </Grid.Column>
          <Grid.Column>
            <FormFields.Select
              control={control}
              name={'housingType'}
              withoutAsterisk
              rules={{
                required: true
              }}
              label={t('Installation form')}
              noErrorMessage
              options={housingTypes.map(housingType => ({ value: housingType.key, text: housingType.label }))}
              placeholder={t('Please select')}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  }

  function getSource(breed) {
    switch (breed.id) {
      case 1:
        return BrowWhiteCow
      case 2:
        return GrayCow
      case 3:
        return BrowCow
      case 4:
        return BlackCow
    }
  }

  function renderRaceSlider() {
    return <div style={{ position: 'absolute', left: 31, top: 1 }}>
      <RaceSlider />
    </div>
  }

  function renderRace() {
    return <Form.Field>
      <label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>{t('Race')}</div>
          <InfoPopup
            content={t('First, click on the main breed you have on your farm.')}
            styleIcon={{ fontSize: '1.2em' }}
            position={'top center'}
          />
        </div>
      </label>
      <Form.Group widths={'equal'} style={{ alignItems: 'center' }}>
        {breeds?.map((breed) => {
          const index = watchBreedField?.findIndex(breedSelected => breedSelected.id === breed.id)
          const maxChecked = watchBreedField?.length === 2
          const displayPercent = maxChecked && index >= 0
          return <Form.Field key={breed.id} style={{ position: 'relative' }}>
            <Checkbox
              label={
                <label style={{ whiteSpace: 'nowrap', minWidth: 200 }}>
                  <img
                    src={getSource(breed)}
                    style={{
                      width: 100,
                      marginRight: 10,
                      height: 100,
                      opacity: maxChecked && index < 0 ? '0.7' : 1,
                      marginTop: maxChecked && index === 1 ? 26 : 20
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: 70,
                      display: 'flex',
                      top: index > 0 ? '-22px' : 0,
                      minWidth: 200
                    }}>
                    <div>{`${breed.label} ${displayPercent ? `(${watchBreedField[index].percent} %)` : ''}`}</div>
                  </div>

                </label>
              }
              disabled={isLoadingPredefinedData || (maxChecked && index < 0)}
              checked={index >= 0}
              onChange={(e, data) => {
                // setting main race
                if (data.checked && watchBreedField.length === 0) {
                  fetchPredefinedData([...watchBreedField, {
                    ...breed,
                    percent: watchBreedField.length === 0 ? '100' : ''
                  }])
                  // setting second race
                } else if (data.checked && watchBreedField.length > 0) {
                  setValue('breeds', [...watchBreedField, {
                    ...breed,
                    percent: '0'
                  }])
                  //deselecting main race while second checked
                } else if (watchBreedField.length > 0 && index === 0) {
                  reset({
                    milkPrice,
                    fat,
                    protein,
                    totalMilkAmount,
                    breeds: []
                  })
                  //deselecting second reace checked
                } else {
                  const tempBreeds = watchBreedField.filter(breedSelected => breedSelected.id !== breed.id)
                  if (tempBreeds[0]) {
                    tempBreeds[0].percent = '100'
                  }
                  setValue('breeds', tempBreeds)
                }
              }}
            />
            {maxChecked && index === 1 && renderRaceSlider()}
          </Form.Field>
        })}
      </Form.Group>
    </Form.Field>
  }

  return <>
    {renderRace()}
    {watchBreedField.length > 0 && !isLoadingPredefinedData && renderFields()}
  </>
}
export default MilkQuantity